import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, shareReplay, tap } from 'rxjs/operators';
import { CentrexExtensionRuleModel } from '../models/centrex-extension-rule.model';
import { ApiService } from './api.service';
import { ServiceBase } from './service-base';

@Injectable({
  providedIn: 'root',
})
export class CentrexExtensionRulesService extends ServiceBase {
  private rulesCache = new Map<
    string,
    Observable<CentrexExtensionRuleModel[]>
  >();

  constructor(
    private http: HttpClient,
    private apiService: ApiService,
  ) {
    super();
  }

  private createHeaders(additionalHeaders?: {
    [key: string]: string;
  }): HttpHeaders {
    const token = this.apiService.getAccessToken();
    let headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    });

    if (additionalHeaders) {
      Object.entries(additionalHeaders).forEach(([key, value]) => {
        headers = headers.set(key, value);
      });
    }

    return headers;
  }

  private getHeaders(organizationId: string, platform: string): HttpHeaders {
    return this.createHeaders({
      'X-OrganizationId': organizationId,
      'X-CentrexPlatform': platform,
    });
  }

  private handleError(error: any): Observable<never> {
    console.error('Erreur CentrexExtensionRulesService', error);
    return throwError(() => error);
  }

  getExtensionRules(
    organizationId: string,
    platform: string,
  ): Observable<CentrexExtensionRuleModel[]> {
    const cacheKey = `${organizationId}_${platform}`;

    // retourne le cache si présent
    if (this.rulesCache.has(cacheKey)) {
      return this.rulesCache.get(cacheKey)!;
    }

    const url = `${this.apiUrl}/api/centrex/extensions/rules`;

    const headers = this.getHeaders(organizationId, platform);

    const request$ = this.http
      .get<CentrexExtensionRuleModel[]>(url, { headers })
      .pipe(
        shareReplay(1),

        catchError((error) => {
          this.rulesCache.delete(cacheKey);
          return this.handleError(error);
        }),
      );

    this.rulesCache.set(cacheKey, request$);

    return request$;
  }

  getGlobalRule(
    organizationId: string,
    platform: string,
    ruleId: string,
  ): Observable<CentrexExtensionRuleModel> {
    const url = `${this.apiUrl}/api/centrex/extensions/rules/${ruleId}`;
    const headers = this.getHeaders(organizationId, platform);
    return this.http
      .get<CentrexExtensionRuleModel>(url, { headers })
      .pipe(catchError(this.handleError));
  }

  getRulesForExtension(
    organizationId: string,
    platform: string,
    extensionId: string,
  ): Observable<CentrexExtensionRuleModel[]> {
    const url = `${this.apiUrl}/api/centrex/extensions/${extensionId}/rules`;
    const headers = this.getHeaders(organizationId, platform);
    return this.http
      .get<CentrexExtensionRuleModel[]>(url, { headers })
      .pipe(catchError(this.handleError));
  }

  getExtensionRule(
    organizationId: string,
    platform: string,
    extensionId: string,
    ruleId: string,
  ): Observable<CentrexExtensionRuleModel> {
    const url = `${this.apiUrl}/api/centrex/extensions/${extensionId}/rules/${ruleId}`;
    const headers = this.getHeaders(organizationId, platform);
    return this.http
      .get<CentrexExtensionRuleModel>(url, { headers })
      .pipe(catchError(this.handleError));
  }

  createGlobalRule(
    organizationId: string,
    platform: string,
    rule: CentrexExtensionRuleModel,
  ): Observable<boolean> {
    const url = `${this.apiUrl}/api/centrex/extensions/rules`;
    const headers = this.getHeaders(organizationId, platform);
    return this.http.post<boolean>(url, rule, { headers }).pipe(
      tap(() => {
        const cacheKey = `${organizationId}_${platform}`;

        this.rulesCache.delete(cacheKey);
      }),

      catchError(this.handleError),
    );
  }

  createExtensionRule(
    organizationId: string,
    platform: string,
    extensionId: string,
    rule: CentrexExtensionRuleModel,
  ): Observable<boolean> {
    const url = `${this.apiUrl}/api/centrex/extensions/${extensionId}/rules`;
    const headers = this.getHeaders(organizationId, platform);
    return this.http.post<boolean>(url, rule, { headers }).pipe(
      tap(() => {
        const cacheKey = `${organizationId}_${platform}`;

        this.rulesCache.delete(cacheKey);
      }),

      catchError(this.handleError),
    );
  }

  updateGlobalRule(
    organizationId: string,
    platform: string,
    ruleId: string,
    rule: CentrexExtensionRuleModel,
  ): Observable<boolean> {
    const url = `${this.apiUrl}/api/centrex/extensions/rules/${ruleId}`;
    const headers = this.getHeaders(organizationId, platform);
    return this.http.patch<boolean>(url, rule, { headers }).pipe(
      tap(() => {
        const cacheKey = `${organizationId}_${platform}`;

        this.rulesCache.delete(cacheKey);
      }),

      catchError(this.handleError),
    );
  }

  updateExtensionRule(
    organizationId: string,
    platform: string,
    extensionId: string,
    ruleId: string,
    rule: CentrexExtensionRuleModel,
  ): Observable<boolean> {
    const url = `${this.apiUrl}/api/centrex/extensions/${extensionId}/rules/${ruleId}`;
    const headers = this.getHeaders(organizationId, platform);
    return this.http.patch<boolean>(url, rule, { headers }).pipe(
      tap(() => {
        const cacheKey = `${organizationId}_${platform}`;

        this.rulesCache.delete(cacheKey);
      }),

      catchError(this.handleError),
    );
  }

  deleteGlobalRule(
    organizationId: string,
    platform: string,
    ruleId: string,
  ): Observable<boolean> {
    const url = `${this.apiUrl}/api/centrex/extensions/rules/${ruleId}`;
    const headers = this.getHeaders(organizationId, platform);
    return this.http.delete<boolean>(url, { headers }).pipe(
      tap(() => {
        const cacheKey = `${organizationId}_${platform}`;

        this.rulesCache.delete(cacheKey);
      }),

      catchError(this.handleError),
    );
  }

  deleteExtensionRule(
    organizationId: string,
    platform: string,
    extensionId: string,
    ruleId: string,
  ): Observable<boolean> {
    const url = `${this.apiUrl}/api/centrex/extensions/${extensionId}/rules/${ruleId}`;
    const headers = this.getHeaders(organizationId, platform);
    return this.http.delete<boolean>(url, { headers }).pipe(
      tap(() => {
        const cacheKey = `${organizationId}_${platform}`;

        this.rulesCache.delete(cacheKey);
      }),

      catchError(this.handleError),
    );
  }

  clearCache(): void {
    this.rulesCache.clear();
  }
}
